import type { SegmentLineStyle } from "@/lib/routeCostCalculation"

/** `[lng, lat]`, the order Mapbox takes. */
export type LngLat = [number, number]
export type LngLatBounds = [LngLat, LngLat]

/** Stops closer than this (~10m, in degrees) are one place on the map. */
export const OVERLAP_DEGREES = 0.0001

/** Widened to at least `minExtent` degrees per axis so a lone pin doesn't frame to street level. */
export function boundsOf(points: LngLat[], minExtent = 0): LngLatBounds | null {
  if (points.length === 0) return null
  const lngs = points.map((p) => p[0])
  const lats = points.map((p) => p[1])
  let minLng = Math.min(...lngs)
  let maxLng = Math.max(...lngs)
  let minLat = Math.min(...lats)
  let maxLat = Math.max(...lats)

  if (maxLng - minLng < minExtent) {
    const centre = (minLng + maxLng) / 2
    minLng = centre - minExtent / 2
    maxLng = centre + minExtent / 2
  }
  if (maxLat - minLat < minExtent) {
    const centre = (minLat + maxLat) / 2
    minLat = centre - minExtent / 2
    maxLat = centre + minExtent / 2
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export function edgePadding(px: number) {
  return { top: px, bottom: px, left: px, right: px }
}

export function overlappingStopIds(
  stops: { id: string; lat: number; lng: number }[],
): Map<string, string[]> {
  const overlaps = new Map<string, string[]>()
  for (let i = 0; i < stops.length; i++) {
    for (let j = i + 1; j < stops.length; j++) {
      const dLat = stops[j].lat - stops[i].lat
      const dLng = stops[j].lng - stops[i].lng
      if (Math.sqrt(dLat * dLat + dLng * dLng) >= OVERLAP_DEGREES) continue
      if (!overlaps.has(stops[i].id)) overlaps.set(stops[i].id, [])
      overlaps.get(stops[i].id)!.push(stops[j].id)
      if (!overlaps.has(stops[j].id)) overlaps.set(stops[j].id, [])
      overlaps.get(stops[j].id)!.push(stops[i].id)
    }
  }
  return overlaps
}

export function segmentLines(
  stops: { lat: number; lng: number; sequence: number }[],
  styles: SegmentLineStyle[],
) {
  const sorted = [...stops].sort((a, b) => a.sequence - b.sequence)
  const features = sorted.slice(0, -1).map((from, i) => {
    const to = sorted[i + 1]
    const lineStyle: SegmentLineStyle = styles[i] ?? "grey"
    return {
      type: "Feature" as const,
      properties: { lineStyle },
      geometry: {
        type: "LineString" as const,
        coordinates: [
          [from.lng, from.lat],
          [to.lng, to.lat],
        ] as LngLat[],
      },
    }
  })
  return { type: "FeatureCollection" as const, features }
}
